import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import MemberHoverCard from "@/components/MemberHoverCard";
import Avatar from "@/components/Avatar";
import Linkify from "@/components/Linkify";
import ReportButton from "@/components/ReportButton";
import { useAuth } from "@/contexts/AuthContext";
import Uploader from "@/components/Uploader";
import MediaView from "@/components/MediaView";
import FileCard from "@/components/FileCard";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import AudioPlayer from "@/components/AudioPlayer";
import { parseYouTubeId } from "@/lib/youtube";
import { Heart, MessageCircle, Send, Sparkles, HandHeart, Trash2, Pencil, Check, X, Youtube, Music, Camera, Palette, Link as LinkIcon, Users } from "lucide-react";
import { toast } from "sonner";
import UpgradeModal from "@/components/UpgradeModal";
import { POST_THEMES, getPostTheme, THEMED_MAX_CHARS } from "@/lib/postThemes";
import { ShareCardDialog } from "@/components/ShareCardDialog";
import { useNavigate } from "react-router-dom";

const REACTIONS = [
  { key: "amen", label: "Amen", icon: HandHeart },
  { key: "heart", label: "Love", icon: Heart },
  { key: "praise", label: "Praise", icon: Sparkles },
];

function SpawnGroupDialog({ open, post, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && post) {
      const c = (post.content || "").trim();
      setName(c.length > 40 ? c.slice(0, 40) + "…" : (c || "Prayer circle"));
    }
  }, [open, post]);

  if (!open || !post) return null;

  // Every 'amen' reactor + the post author becomes a founding member.
  const amenCount = (post.reactions && post.reactions.amen) || 0;
  const founding = amenCount + 1; // + the author

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/spawn-group`, { name });
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create group.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="spawn-group-dialog" role="dialog" aria-modal="true">
      <div className="jj-card w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[var(--jj-surface)]" aria-label="Close"><X className="h-4 w-4" strokeWidth={1.6} /></button>
        <div className="overline text-burgundy">Prayer group</div>
        <h3 className="font-serif text-2xl text-jjblue mt-1">Turn this into a group</h3>
        <p className="text-sm text-[var(--jj-muted)] mt-1">
          A new prayer group will be born with{" "}
          <strong className="text-jjblue">{founding} founding member{founding !== 1 ? "s" : ""}</strong>
          {amenCount > 0 ? ` (you + ${amenCount} who said Amen)` : " (just you for now)"}.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="text-xs text-jjblue/80">Group name</label>
          <input className="input-jj" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-testid="spawn-group-name" />
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy || !name.trim()} className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2" data-testid="spawn-group-submit">
              <Users className="h-4 w-4" strokeWidth={1.6} /> {busy ? "Creating…" : "Create group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Post({ post, currentUser, onChange, onDelete }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.content);
  const [editDraftBg, setEditDraftBg] = useState(post.background || "none");
  const [editBgOpen, setEditBgOpen] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [spawnOpen, setSpawnOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthorOrAdmin = currentUser?.id === post.author?.id || currentUser?.role === "admin";
  const canDeletePost = isAuthorOrAdmin;
  const canEditPost = isAuthorOrAdmin;
  const canSpawnGroup = isAuthorOrAdmin;
  const hasMedia = (post.media_paths?.length || 0) > 0;
  const themeId = post.background && post.background !== "none" ? post.background : null;
  const showThemedHero = !!themeId && !hasMedia && !editing && (post.content?.length || 0) <= THEMED_MAX_CHARS;
  const theme = getPostTheme(themeId);

  const copyPermalink = async () => {
    const url = `${window.location.origin}/feed?post=${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share the moment.");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const loadComments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/posts/${post.id}/comments`);
      setComments(data);
    } finally { setLoading(false); }
  };

  const toggleComments = () => {
    if (!showComments) loadComments();
    setShowComments(!showComments);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const { data } = await api.post(`/posts/${post.id}/comments`, { content: text });
    setComments([...comments, data]);
    setText("");
    onChange?.();
  };

  const react = async (emoji) => {
    const { data } = await api.post(`/posts/${post.id}/react?emoji=${emoji}`);
    onChange?.(data);
  };

  const removePost = async () => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      onDelete?.(post.id);
      toast.success("Post deleted");
    } catch {
      toast.error("Could not delete post");
    }
  };

  const removeComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${c.id}`);
      setComments(comments.filter((x) => x.id !== c.id));
      onChange?.();
    } catch {
      toast.error("Could not delete comment");
    }
  };

  const saveEdit = async () => {
    if (!draft.trim()) return;
    try {
      const { data } = await api.patch(`/posts/${post.id}`, { content: draft, background: editDraftBg });
      setEditing(false);
      setEditBgOpen(false);
      onChange?.(data);
      toast.success("Post updated");
    } catch {
      toast.error("Could not update post");
    }
  };

  const startEditComment = (c) => {
    setEditingCommentId(c.id);
    setCommentDraft(c.content);
  };

  const saveEditComment = async (c) => {
    if (!commentDraft.trim()) return;
    try {
      const { data } = await api.patch(`/comments/${c.id}`, { content: commentDraft });
      setComments(comments.map((x) => x.id === c.id ? data : x));
      setEditingCommentId(null);
      toast.success("Comment updated");
    } catch {
      toast.error("Could not update comment");
    }
  };

  return (
    <article
      id={`post-${post.id}`}
      className="jj-card p-6 sm:p-8 fade-up scroll-mt-24"
      data-testid={`post-${post.id}`}
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {post.author?.id ? (
            <Link to={`/u/${post.author.id}`} className="hover:opacity-80 transition" data-testid={`post-author-avatar-${post.id}`}>
              <Avatar user={post.author} size={40} />
            </Link>
          ) : (
            <Avatar user={post.author} size={40} />
          )}
          <div className="min-w-0">
            {post.author?.id ? (
              <MemberHoverCard user={post.author} currentUserId={currentUser?.id}>
                <Link to={`/u/${post.author.id}`} className="text-jjblue font-medium hover:text-burgundy hover:underline" data-testid={`post-author-name-${post.id}`}>{post.author?.name}</Link>
              </MemberHoverCard>
            ) : (
              <div className="text-jjblue font-medium">{post.author?.name}</div>
            )}
            <div className="text-xs text-[var(--jj-muted)]">
              <span className="text-gold">{post.author?.tier}</span> · {new Date(post.created_at).toLocaleString()}
            </div>
            {post.author?.bio && (
              <div className="mt-0.5 text-xs text-[var(--jj-muted)] italic line-clamp-1 max-w-[34rem]" data-testid={`post-author-bio-${post.id}`}>
                {post.author.bio}
              </div>
            )}
          </div>
        </div>
        {canDeletePost && (
          <div className="flex items-center gap-1">
            {canEditPost && !editing && (
              <button onClick={() => { setEditing(true); setDraft(post.content); }} aria-label="Edit post"
                className="text-[var(--jj-muted)] hover:text-burgundy p-1.5 rounded-full transition-colors"
                data-testid={`post-edit-${post.id}`}>
                <Pencil className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
            <button onClick={removePost} aria-label="Delete post"
              className="text-[var(--jj-muted)] hover:text-burgundy p-1.5 rounded-full transition-colors"
              data-testid={`post-delete-${post.id}`}>
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
      </header>
      {editing ? (
        <div className="mt-5">
          <textarea rows={4} className="input-jj !rounded-xl resize-none"
            value={draft} onChange={(e) => setDraft(e.target.value)}
            data-testid={`post-edit-input-${post.id}`} />
          {!hasMedia && (
            <div className="mt-3">
              <button type="button" onClick={() => setEditBgOpen((v) => !v)}
                className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
                data-testid={`post-edit-theme-toggle-${post.id}`}>
                <Palette className="h-3.5 w-3.5" strokeWidth={1.6} /> Background {editDraftBg !== "none" ? `· ${editDraftBg}` : ""}
              </button>
              {editBgOpen && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid={`post-edit-theme-picker-${post.id}`}>
                  {POST_THEMES.map((t) => (
                    <button key={t.id} type="button" onClick={() => setEditDraftBg(t.id)}
                      title={t.label}
                      aria-label={`Background ${t.label}`}
                      className={`h-8 w-8 rounded-full border-2 transition ${editDraftBg === t.id ? "border-[var(--jj-blue)] scale-110" : "border-white hover:scale-105"} shadow-sm`}
                      style={{ background: t.swatch }}
                      data-testid={`post-edit-theme-${t.id}-${post.id}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => { setEditing(false); setEditBgOpen(false); setEditDraftBg(post.background || "none"); }} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`post-edit-cancel-${post.id}`}>
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button onClick={saveEdit} className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`post-edit-save-${post.id}`}>
              <Check className="h-3.5 w-3.5" /> Save
            </button>
          </div>
        </div>
      ) : showThemedHero ? (
        <button
          type="button"
          onClick={copyPermalink}
          className="mt-5 w-full rounded-2xl px-6 py-10 sm:py-14 text-center relative overflow-hidden group focus:outline-none focus:ring-2 focus:ring-[var(--jj-gold)]"
          style={{ background: theme.background, color: theme.text }}
          data-testid={`post-themed-hero-${post.id}`}
          aria-label="Copy shareable link to this post"
        >
          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{ background: "radial-gradient(circle at center, rgba(255,255,255,0.12) 0%, transparent 65%)" }} />
          <p className="font-serif text-2xl sm:text-3xl leading-snug relative">
            <Linkify>{post.content}</Linkify>
          </p>
          <span
            className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] opacity-70 group-hover:opacity-100 transition-opacity relative"
            style={{ color: theme.text }}
          >
            <LinkIcon className="h-3 w-3" strokeWidth={1.8} /> Tap to share
          </span>
        </button>
      ) : (
        <p className="mt-5 text-jjblue/90 leading-relaxed"><Linkify>{post.content}</Linkify></p>
      )}
      {post.media_paths?.length > 0 && (
        <div className="mt-5 space-y-3">
          {(() => {
            const items = post.media_paths.map((p, i) => ({ path: p, kind: post.media_types?.[i] || "image" }));
            const visual = items.filter((m) => m.kind === "image" || m.kind === "video");
            const yts = items.filter((m) => m.kind === "youtube");
            const audios = items.filter((m) => m.kind === "audio");
            const docs = items.filter((m) => m.kind !== "image" && m.kind !== "video" && m.kind !== "youtube" && m.kind !== "audio" && m.kind !== "link");
            return (
              <>
                {visual.length > 0 && (
                  <div className={`grid gap-3 ${visual.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {visual.map((m) => (
                      <MediaView key={m.path} path={m.path} kind={m.kind}
                        className="rounded-xl w-full max-h-[480px] object-cover border border-jj" />
                    ))}
                  </div>
                )}
                {yts.map((m) => <YouTubeEmbed key={m.path} videoId={m.path} />)}
                {audios.map((m) => <AudioPlayer key={`a-${m.path}`} path={m.path} />)}
                {docs.map((m) => <FileCard key={m.path} path={m.path} />)}
              </>
            );
          })()}
        </div>
      )}
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        {REACTIONS.map((r) => (
          <button key={r.key} onClick={() => react(r.key)} data-testid={`react-${r.key}-${post.id}`}
            className={`btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5 ${post.my_reaction === r.key ? "!border-[var(--jj-gold)] !bg-white" : ""}`}>
            <r.icon className="h-3.5 w-3.5" strokeWidth={1.6} /> {r.label} <span className="text-[var(--jj-muted)]">{post.reactions?.[r.key] || 0}</span>
          </button>
        ))}
        <button onClick={toggleComments} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`toggle-comments-${post.id}`}>
          <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.6} /> Comments · {post.comments_count}
        </button>
        <button onClick={copyPermalink} aria-label="Copy shareable link"
          className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
          data-testid={`post-share-${post.id}`}>
          <LinkIcon className="h-3.5 w-3.5" strokeWidth={1.6} /> Share
        </button>
        {showThemedHero && (
          <button onClick={() => setShareOpen(true)} aria-label="Send as image"
            className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            data-testid={`post-send-image-${post.id}`}>
            <Send className="h-3.5 w-3.5" strokeWidth={1.6} /> Send
          </button>
        )}
        {canSpawnGroup && (
          <button onClick={() => setSpawnOpen(true)} aria-label="Turn this post into a prayer group"
            className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            data-testid={`post-spawn-group-${post.id}`}>
            <Users className="h-3.5 w-3.5" strokeWidth={1.6} /> Turn into group
          </button>
        )}
        {currentUser?.id && currentUser.id !== post.author?.id && (
          <span className="ml-auto"><ReportButton targetType="post" targetId={post.id} /></span>
        )}
      </div>
      {showComments && (
        <div className="mt-5 border-t border-jj pt-4 space-y-3">
          {loading && <div className="text-sm text-[var(--jj-muted)]">Loading…</div>}
          {comments.map((c) => {
            const canDelComment = currentUser?.id === c.author?.id || currentUser?.role === "admin";
            const isEditingThis = editingCommentId === c.id;
            return (
              <div key={c.id} className="text-sm flex items-start justify-between gap-3 group">
                {isEditingThis ? (
                  <div className="flex-1 flex gap-2">
                    <input className="input-jj !py-1.5 text-sm" value={commentDraft}
                      onChange={(e) => setCommentDraft(e.target.value)}
                      data-testid={`comment-edit-input-${c.id}`} />
                    <button onClick={() => saveEditComment(c)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`comment-edit-save-${c.id}`}><Check className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setEditingCommentId(null)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`comment-edit-cancel-${c.id}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <>
                    <div>
                      {c.author?.id ? (
                        <MemberHoverCard user={c.author} currentUserId={currentUser?.id}>
                          <Link to={`/u/${c.author.id}`} className="font-medium text-jjblue hover:text-burgundy hover:underline" data-testid={`comment-author-link-${c.id}`}>{c.author?.name}</Link>
                        </MemberHoverCard>
                      ) : (
                        <span className="font-medium text-jjblue">{c.author?.name}</span>
                      )}
                      <span className="text-jjblue/90">: {c.content}</span>
                      {c.edited && <span className="ml-2 text-xs text-[var(--jj-muted)] italic">(edited)</span>}
                    </div>
                    {canDelComment && (
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition">
                        <button onClick={() => startEditComment(c)} aria-label="Edit comment"
                          className="text-[var(--jj-muted)] hover:text-burgundy" data-testid={`comment-edit-${c.id}`}>
                          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                        <button onClick={() => removeComment(c)} aria-label="Delete comment"
                          className="text-[var(--jj-muted)] hover:text-burgundy" data-testid={`comment-delete-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
          <form onSubmit={send} className="flex gap-2 pt-2">
            <input className="input-jj" placeholder="Encourage your brother or sister…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`comment-input-${post.id}`} />
            <button className="btn-primary !py-2 !px-4" data-testid={`comment-submit-${post.id}`}><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
      <ShareCardDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        content={post.content}
        background={post.background}
        authorName={post.author?.name}
        kind="post"
      />
      <SpawnGroupDialog
        open={spawnOpen}
        post={post}
        onClose={() => setSpawnOpen(false)}
        onCreated={(g) => {
          toast.success(`Prayer group "${g.name}" created`);
          navigate(`/groups/${g.id}`);
        }}
      />
    </article>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [media, setMedia] = useState([]); // [{path, kind}]
  const [busy, setBusy] = useState(false);
  const [ytOpen, setYtOpen] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  const [upgrade, setUpgrade] = useState(null); // { tier, reason } or null
  const [background, setBackground] = useState("none");
  const [themeOpen, setThemeOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/posts");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  // Deep link support: /feed?post=<id> scrolls the linked post into view.
  useEffect(() => {
    if (!posts.length) return;
    const params = new URLSearchParams(window.location.search);
    const target = params.get("post");
    if (!target) return;
    const el = document.getElementById(`post-${target}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[var(--jj-gold)]", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-[var(--jj-gold)]", "ring-offset-2"), 2600);
    }
  }, [posts]);

  const hasMedia = media.length > 0;
  // Background themes are meant for short, greeting-card style posts. If the
  // member attaches media, we quietly clear the theme so the artwork isn't
  // fighting a coloured backdrop.
  const activeBackground = hasMedia ? "none" : background;
  const activeTheme = getPostTheme(activeBackground);
  const themedPreview = activeBackground !== "none" && !hasMedia && content.length > 0 && content.length <= THEMED_MAX_CHARS;

  const attachYouTube = () => {
    const id = parseYouTubeId(ytUrl);
    if (!id) {
      toast.error("Not a valid YouTube link");
      return;
    }
    if (media.some((m) => m.kind === "youtube" && m.path === id)) {
      toast.error("That video is already attached");
      return;
    }
    setMedia([...media, { path: id, kind: "youtube", filename: `YouTube · ${id}` }]);
    setYtUrl("");
    setYtOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post("/posts", {
        content,
        media_paths: media.map((m) => m.path),
        media_types: media.map((m) => m.kind),
        background: activeBackground,
      });
      setContent(""); setMedia([]); setBackground("none"); setThemeOpen(false);
      load();
      toast.success("Shared with the community");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const covenant = err?.response?.data?.covenant_block;
      if (status === 403 && typeof detail === "string") {
        const tier = /Disciple/.test(detail) ? "Disciple" : "Believer";
        setUpgrade({ tier, reason: detail });
      } else if (status === 422 && covenant) {
        toast.error(detail);
      } else {
        toast.error("Could not share, please try again");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="overline text-burgundy">Community Feed</div>
      <h1 className="font-serif text-4xl text-jjblue mt-2">Memories, moments, and miracles</h1>
      <p className="mt-2 text-[var(--jj-text-secondary)]">Hello {user?.name}. What is the Lord doing in you today?</p>

      {user && !user.avatar_path && !localStorage.getItem("jj_dismissed_avatar_nudge") && (
        <div className="mt-6 jj-card !p-4 flex items-center gap-4 bg-gradient-to-r from-cream to-cream-surface" data-testid="avatar-nudge">
          <span className="h-11 w-11 rounded-full bg-gold/20 text-burgundy flex items-center justify-center shrink-0">
            <Camera className="h-5 w-5" strokeWidth={1.6} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-jjblue">Put a face to your name</div>
            <p className="text-xs text-[var(--jj-muted)] mt-0.5">Members open up faster when they can see who&rsquo;s sharing the prayer. Takes 10 seconds.</p>
          </div>
          <Link to="/profile" className="btn-primary !py-2 !px-4 text-sm shrink-0" data-testid="avatar-nudge-cta">Add photo</Link>
          <button
            onClick={(e) => { e.currentTarget.parentElement.style.display = "none"; localStorage.setItem("jj_dismissed_avatar_nudge", "1"); }}
            className="text-[var(--jj-muted)] hover:text-burgundy shrink-0"
            aria-label="Dismiss"
            data-testid="avatar-nudge-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <form onSubmit={submit} className="jj-card p-6 mt-8" data-testid="composer">
        {themedPreview ? (
          <div
            className="rounded-2xl px-6 py-8 sm:py-12 text-center relative overflow-hidden transition-colors duration-300"
            style={{ background: activeTheme.background, color: activeTheme.text }}
            data-testid="composer-themed-preview"
          >
            <textarea
              rows={3}
              maxLength={THEMED_MAX_CHARS}
              placeholder="Share a praise, a memory, a verse…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full bg-transparent border-none focus:outline-none resize-none font-serif text-2xl sm:text-3xl leading-snug text-center placeholder:opacity-60"
              style={{ color: activeTheme.text }}
              data-testid="composer-content"
            />
            <div className="mt-2 text-xs opacity-70" style={{ color: activeTheme.text }}>
              {content.length}/{THEMED_MAX_CHARS} · themed posts stay short and sharable
            </div>
          </div>
        ) : (
          <textarea rows={3} className="input-jj !rounded-xl resize-none" placeholder="Share a praise, a memory, a verse…"
            value={content} onChange={(e) => setContent(e.target.value)} data-testid="composer-content" />
        )}
        {themeOpen && !hasMedia && (
          <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="composer-theme-picker">
            <span className="text-xs text-[var(--jj-muted)] mr-1">Background:</span>
            {POST_THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setBackground(t.id)}
                title={t.label}
                aria-label={`Background ${t.label}`}
                className={`h-9 w-9 rounded-full border-2 transition ${background === t.id ? "border-[var(--jj-blue)] scale-110" : "border-white hover:scale-105"} shadow-sm`}
                style={{ background: t.swatch }}
                data-testid={`composer-theme-${t.id}`}
              />
            ))}
          </div>
        )}
        {hasMedia && background !== "none" && (
          <div className="mt-2 text-xs text-[var(--jj-muted)] italic">
            Backgrounds are for text-only posts. We&rsquo;ll keep your attachment and drop the theme.
          </div>
        )}
        {media.length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap text-xs text-[var(--jj-muted)] items-center">
            {media.map((m) => (
              <span key={`${m.kind}-${m.path}`} className="px-2 py-1 bg-cream-surface rounded-full flex items-center gap-1.5">
                {m.kind === "youtube" && <Youtube className="h-3 w-3 text-burgundy" strokeWidth={1.6} />}
                {m.kind} · {m.filename || "attached"}
                <button type="button" onClick={() => setMedia(media.filter((x) => !(x.path === m.path && x.kind === m.kind)))}
                  className="hover:text-burgundy" aria-label="Remove attachment">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {ytOpen && (
          <div className="mt-3 flex gap-2" data-testid="yt-input-row">
            <input className="input-jj text-sm" placeholder="Paste a YouTube link (youtube.com/watch?v=…, youtu.be/…, /shorts/…)"
              value={ytUrl} onChange={(e) => setYtUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); attachYouTube(); } }}
              data-testid="yt-url-input" />
            <button type="button" onClick={attachYouTube} className="btn-primary !py-2 !px-4 text-sm" data-testid="yt-attach">Attach</button>
            <button type="button" onClick={() => { setYtOpen(false); setYtUrl(""); }} className="btn-secondary !py-2 !px-3 text-sm" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <Uploader accept="image/*,video/*" label="Photo / video"
              onUploaded={(d) => setMedia([...media, d])} testId="composer-upload" />
            <Uploader accept=".pdf,.doc,.docx,.txt,.md,.ipynb,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/x-ipynb+json"
              label="Document upload" icon="doc"
              onUploaded={(d) => setMedia([...media, d])} testId="composer-upload-doc" />
            <Uploader accept="audio/*,.mp3,.m4a,.wav,.ogg,.aac,.flac"
              label="Music / audio" icon="music"
              onUploaded={(d) => setMedia([...media, d])} testId="composer-upload-audio" />
            <button type="button" onClick={() => setYtOpen(!ytOpen)}
              className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-2" data-testid="composer-yt-btn">
              <Youtube className="h-4 w-4" strokeWidth={1.5} /> YouTube
            </button>
            <button type="button" onClick={() => setThemeOpen((v) => !v)}
              disabled={hasMedia}
              title={hasMedia ? "Remove attachments to use a background theme" : "Add a background theme"}
              className={`btn-secondary !py-2 !px-4 text-sm flex items-center gap-2 ${themeOpen ? "!border-[var(--jj-gold)] !bg-white" : ""} ${hasMedia ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="composer-theme-btn">
              <Palette className="h-4 w-4" strokeWidth={1.5} /> Background
              {background !== "none" && !hasMedia && (
                <span
                  className="ml-1 h-3 w-3 rounded-full border border-white shadow"
                  style={{ background: activeTheme.swatch }}
                  aria-hidden
                />
              )}
            </button>
          </div>
          <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="composer-submit">{busy ? "Sharing…" : "Share"}</button>
        </div>
      </form>

      <div className="mt-8 space-y-6">
        {posts.length === 0 && <div className="text-center text-[var(--jj-muted)] py-10">Be the first to share something beautiful.</div>}
        {posts.map((p) => (
          <Post key={p.id} post={p} currentUser={user}
            onDelete={(id) => setPosts(posts.filter((x) => x.id !== id))}
            onChange={(updated) => {
              if (updated) setPosts(posts.map((x) => x.id === updated.id ? { ...x, ...updated } : x));
              else load();
            }} />
        ))}
      </div>

      <UpgradeModal
        open={!!upgrade}
        onClose={() => setUpgrade(null)}
        tier={upgrade?.tier}
        reason={upgrade?.reason}
      />
    </div>
  );
}
